import { StandardModal } from "@classdojo/web/components/modals";
import { TextField } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { translate } from "@classdojo/web/utils/translate";
import React from "react";
import type { AnyAction } from "redux";
import TimerDisplay from "./TimerDisplay";
import { appMetadataTimer, RECEIVE_TIME, Timer } from "src/pods/timer";
import AppModal from "src/shared/components/AppModal";
import AppPage from "src/shared/components/AppPage";
import logEvent from "src/utils/logEvent";

const TIMES: Timer[] = [{ seconds: 15 }, { seconds: 30 }, { seconds: 60 }, { seconds: 300 }, { seconds: 600 }];

const initialState = {
  selectedTime: null,
};

type TimerRemoteProps = {
  classId: string;
  mountPoint: string;
  displayOpen: (url: string, action?: AnyAction) => void;
  displayClose: VoidFunction;
  remoteClose: VoidFunction;
  sendTime: (time: Timer) => void;
  time: Timer;
  isCasting: boolean;
  isConnected: boolean;
};

type TimerRemoteState = {
  minutesInput?: number | null;
  secondsInput?: number | null;
  selectedTime: Timer | null;
  askForTime?: boolean;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class TimerRemote extends React.Component<TimerRemoteProps, TimerRemoteState> {
  constructor(props: TimerRemoteProps) {
    super(props);
    this.state = initialState;
  }

  componentDidMount() {
    logEvent({
      eventName: "timer.setup.open",
    });
  }

  componentWillUnmount() {
    logEvent({
      eventName: "timer.setup.close",
    });
  }

  render() {
    const { classId, mountPoint, isCasting, isConnected } = this.props;

    function renderTime(time: Timer) {
      const minutes = time.seconds ? Math.floor(time.seconds / 60) : 0;
      const seconds = time.seconds ? String(time?.seconds % 60).padStart(2, "0") : 0;
      return (
        <li key={time.seconds} sx={timesListItemStyle}>
          <button
            data-name="timer_remote:time_remaining"
            sx={timeButtonStyle}
            onClick={() => this.openDisplay(time)}
            aria-label={translate({
              str: "dojo.toolkit:timer.select_specific_time",
              subs: { minutes, seconds },
              fallback: "Select __minutes__ minutes and __seconds__ seconds",
            })}
          >
            <span sx={timeButtonTextStyle}>{`${minutes}:${seconds}`}</span>
          </button>
        </li>
      );
    }

    return (
      <AppPage
        {...appMetadataTimer()}
        classId={classId}
        mountPoint={mountPoint}
        disableFocusTrap={this.state.askForTime}
      >
        <div sx={containerStyle}>
          <section sx={timesStyle}>
            <ul sx={timesListStyle}>
              {TIMES.map(renderTime, this)}
              <li sx={timesListItemStyle}>
                <button
                  data-name="timer_remote:select_custom_time"
                  sx={addTimeButtonStyle}
                  onClick={this.openTimeInput}
                  aria-label={translate({
                    str: "dojo.toolkit:timer.select_custom_time",
                    fallback: "Select custom time",
                  })}
                >
                  <span sx={timeButtonTextStyle}>
                    <span sx={addTimeButtonIconStyle}>{"+"}</span>
                  </span>
                </button>
              </li>
            </ul>
          </section>
          {this.state.askForTime && (
            <StandardModal
              data-name="timer_remote:render:standard_modal"
              headerStyle={{
                display: "none",
              }}
              primaryButton={{
                text: translate({
                  str: "dojo.toolkit:modal_actions.start",
                  fallback: "Start",
                }),
                props: {
                  "data-name": "timer_remote:start",
                  onClick: () => {
                    const { minutesInput, secondsInput } = this.state;
                    this.closeTimeInput();
                    this.openDisplay({
                      seconds: (minutesInput || 0) * 60 + (secondsInput || 0),
                    });
                  },
                },
              }}
              secondaryButton={{
                text: translate({
                  str: "dojo.toolkit:modal_actions.cancel",
                  fallback: "Cancel",
                }),
                props: { onClick: this.closeTimeInput, "data-name": "timer_remove:cancel" },
              }}
            >
              <div sx={{ width: "100%", display: "flex", alignItems: "center" }}>
                <TextField
                  label={translate({
                    str: "dojo.toolkit:timer.minutes",
                    fallback: "Minutes",
                  })}
                  type="number"
                  onChange={this.onMinutesChange}
                  max={60}
                  min={0}
                  sx={{ flexGrow: 1 }}
                />
                <span sx={inputSeparatorStyle}>{":"}</span>
                <TextField
                  label={translate({
                    str: "dojo.toolkit:timer.seconds",
                    fallback: "Seconds",
                  })}
                  type="number"
                  max={60}
                  min={0}
                  onChange={this.onSecondsChange}
                  sx={{ flexGrow: 1 }}
                />
              </div>
            </StandardModal>
          )}
          {this.state.selectedTime && (
            <AppModal
              {...appMetadataTimer()}
              onRequestHide={() => {
                this.props.displayClose();
                this.props.remoteClose();
              }}
              onRequestStart={() => this.props.displayOpen("/apps/display/timer")}
              onRequestStop={() => this.props.displayClose()}
            >
              <TimerDisplay
                shouldRingBell={!(isCasting && isConnected)}
                time={this.props.time}
                sendTime={this.sendTime}
                fullscreen={false}
              />
            </AppModal>
          )}
        </div>
      </AppPage>
    );
  }

  sendTime = (time: Timer) => {
    const payload = {
      ...time,
      startTime: Date.now(),
    };
    this.props.sendTime(payload);
    this.props.displayOpen("/apps/display/timer", { type: RECEIVE_TIME, payload });
  };

  openTimeInput = () => {
    this.setState({ askForTime: true, minutesInput: null, secondsInput: null });
  };

  closeTimeInput = () => {
    this.setState({ askForTime: false });
  };

  onSecondsChange = (value: string) => {
    this.setState({ secondsInput: parseInt(value, 10) || null });
  };

  onMinutesChange = (value: string) => {
    this.setState({ minutesInput: parseInt(value, 10) || null });
  };

  openDisplay = (time: Timer) => {
    this.sendTime(time);

    if (this.props.mountPoint === "mobile") this.setState({ selectedTime: time });
    logEvent({
      eventName: TIMES.includes(time) ? "timer.start" : "timer.start.custom",
      eventValue: String(time.seconds),
    });
  };

  closeDisplay = () => {
    this.setState({ selectedTime: null });
    this.props.displayClose();
  };
}

const containerStyle: ThemeUIStyleObject = {
  padding: RAW_cssValue("20px 0"),
};

const timesStyle: ThemeUIStyleObject = {
  margin: "auto",
};

const timesListStyle: ThemeUIStyleObject = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  margin: "0",
  padding: "0",
  listStyleType: "none",
};

const timesListItemStyle: ThemeUIStyleObject = {
  flex: "0 146px",
  margin: RAW_cssValue("7px"),
};

const timeButtonStyle: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  height: "100px",
  width: "146px",
  margin: "0",
  padding: "0",
  border: "dt_card",
  borderRadius: "dt_radius_s",
  backgroundColor: "dt_background_primary",
  fontSize: "48px",
  fontWeight: "800",
  textAlign: "center",
  verticalAlign: "middle",
  boxShadow: "dt_shadow_shadezies",
  cursor: "pointer",
  color: RAW_cssValue("rgb(87,91,125)"),
  userSelect: "none",
};

const addTimeButtonStyle: ThemeUIStyleObject = {
  ...timeButtonStyle,
  color: RAW_cssValue("rgb(0,174,239)"),
};

const timeButtonTextStyle: ThemeUIStyleObject = {
  flex: "1",
};

const addTimeButtonIconStyle: ThemeUIStyleObject = {
  marginRight: RAW_cssValue("4px"),
  fontSize: "1em",
  verticalAlign: "middle",
};

const inputSeparatorStyle: ThemeUIStyleObject = {
  margin: "dt_s",
  fontWeight: 600,
  fontSize: "18px",
  color: "dt_content_secondary",
};
